import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FilePlus2, Gift, HeartHandshake, ListChecks, Pencil } from "lucide-react";

import { alumniService, type Alumni, type Donation, type Mentorship, type Newsletter, type Options, type Survey, type SurveyResponse } from "../../services/alumniService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field } from "../FormLayout";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { DonationForm, MentorshipForm, NewsletterForm, RespondForm, SurveyForm } from "./AlumniForms";
import {
  DONATION_TYPES,
  MENTORSHIP_STATUS,
  MENTORSHIP_TYPES,
  SURVEY_TYPES,
  day,
  donationConfig,
  labelOf,
  mentorshipConfig,
  mentorshipMoves,
  newsletterConfig,
  surveyConfig,
  surveyState,
  ugx,
} from "./alumniLogic";

const worth = (row: Donation) => (row.amount ? ugx(row.amount) : "—");

/** Gifts from former pupils, and whether each has been thanked. */
export function GivingPanel({ rows, alumni, isOffice, isLoading, isError, onRetry, onChanged }: { rows?: Donation[]; alumni: Alumni[]; isOffice: boolean; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [how, setHow] = useState("");
  const done = (text: string) => { setAdding(false); setViewing(null); setNotice({ tone: "success", text }); onChanged(); };
  const add = useMutation({ mutationFn: alumniService.createDonation, onSuccess: (saved) => done(`Gift from ${saved.alumni_name} recorded.`) });
  const thank = useMutation({ mutationFn: ({ id, text }: { id: number; text: string }) => alumniService.acknowledgeDonation(id, text), onSuccess: (saved) => done(`${saved.alumni_name}'s gift is marked as thanked.`) });
  const total = (rows ?? []).filter((row) => row.donation_type === "monetary").reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
  const columns: RegisterColumn<Donation>[] = [
    { header: "Given", cell: (row) => day(row.donation_date), exportValue: (row) => day(row.donation_date) },
    { header: "From", cell: (row) => <span className="font-medium text-gray-900">{row.alumni_name}</span>, exportValue: (row) => row.alumni_name },
    { header: "Kind", cell: (row) => labelOf(DONATION_TYPES, row.donation_type), exportValue: (row) => labelOf(DONATION_TYPES, row.donation_type) },
    { header: "What", cell: (row) => <span className="line-clamp-1 max-w-xs">{row.description}</span>, exportValue: (row) => row.description },
    { header: "Fund", cell: (row) => row.fund || "—", exportValue: (row) => row.fund ?? "" },
    { header: "Value", cell: (row) => worth(row), exportValue: (row) => Number(row.amount ?? 0), align: "right" },
    { header: "Thanked", cell: (row) => (row.acknowledgment_sent ? <span className="text-emerald-700">Yes</span> : <span className="text-amber-700">Not yet</span>), exportValue: (row) => (row.acknowledgment_sent ? "Yes" : "No") },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title={isOffice ? "Giving" : "Your gifts"}
        description={isOffice ? `Gifts from former pupils${total ? `: ${ugx(total)} in money so far` : ""}. Open one to mark it thanked.` : "What you have given the school. Thank you."}
        actions={isOffice && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { add.reset(); setAdding(true); }}><Gift className="h-4 w-4" /> Record a gift</button>}
        rows={rows}
        config={donationConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => { setHow(""); setViewing(item.id); }}
        openLabel={(item) => (isOffice && !item.acknowledgment_sent ? "Thank" : "Open")}
        noun={["gift", "gifts"]}
        exportName={{ file: "alumni-giving", title: "Alumni Giving" }}
        canExport={isOffice}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No gifts recorded yet."
      />
      {row && (
        <DetailsModal
          title={row.alumni_name}
          headline={{ label: labelOf(DONATION_TYPES, row.donation_type), value: worth(row) === "—" ? row.description : worth(row), note: `Given ${day(row.donation_date)}` }}
          status={{ label: row.acknowledgment_sent ? "Thanked" : "Not yet thanked", dot: row.acknowledgment_sent ? "bg-emerald-500" : "bg-amber-500" }}
          facts={[["For", row.purpose || "—"], ["Fund", row.fund || "—"], ["Received by", row.received_by_name]]}
          sections={[{ title: "What was given", body: row.description }, { title: "Notes", body: row.notes }]}
          onClose={() => setViewing(null)}
        >
          {isOffice && !row.acknowledgment_sent && (
            <ActionPanel title="Mark it thanked" description="Once the giver has been thanked, say how.">
              <Field label="How they were thanked" optional>
                <input value={how} onChange={(event) => setHow(event.target.value)} className="input mt-1" placeholder="e.g. Letter from the head teacher" />
              </Field>
              <div className="flex justify-end"><button type="button" disabled={thank.isPending} className="btn btn-primary" onClick={() => thank.mutate({ id: row.id, text: how })}>Mark thanked</button></div>
              {thank.isError && <p role="alert" className="text-sm text-red-700">{apiErrorMessage(thank.error, "It could not be marked.")}</p>}
            </ActionPanel>
          )}
        </DetailsModal>
      )}
      {adding && <DonationForm alumni={alumni} busy={add.isPending} error={add.isError ? apiErrorMessage(add.error, "The gift could not be recorded.") : undefined} onSave={(data) => add.mutate(data)} onClose={() => setAdding(false)} />}
    </div>
  );
}

/** Former pupils mentoring current learners. */
export function MentorshipsPanel({ rows, alumni, options, isOffice, isLoading, isError, onRetry, onChanged }: { rows?: Mentorship[]; alumni: Alumni[]; options?: Options; isOffice: boolean; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const done = (text: string) => { setAdding(false); setViewing(null); setNotice({ tone: "success", text }); onChanged(); };
  const add = useMutation({ mutationFn: alumniService.createMentorship, onSuccess: (saved) => done(`${saved.mentor_name} now mentors ${saved.mentee_name}.`) });
  const move = useMutation({ mutationFn: ({ id, status, reason }: { id: number; status: string; reason: string }) => alumniService.transitionMentorship(id, status, reason), onSuccess: (saved) => done(`${saved.title}: ${MENTORSHIP_STATUS[saved.status].label.toLowerCase()}.`) });
  const columns: RegisterColumn<Mentorship>[] = [
    { header: "Mentor", cell: (row) => <span className="font-medium text-gray-900">{row.mentor_name}</span>, exportValue: (row) => row.mentor_name },
    { header: "Learner", cell: (row) => row.mentee_name, exportValue: (row) => row.mentee_name },
    { header: "Title", cell: (row) => row.title, exportValue: (row) => row.title },
    { header: "Kind", cell: (row) => labelOf(MENTORSHIP_TYPES, row.mentorship_type), exportValue: (row) => labelOf(MENTORSHIP_TYPES, row.mentorship_type) },
    { header: "Runs", cell: (row) => `${day(row.start_date)} → ${day(row.end_date) || `${row.expected_duration_months} months`}`, exportValue: (row) => `${day(row.start_date)} to ${day(row.end_date) || ""}` },
    { header: "Status", cell: (row) => <StatusDot {...MENTORSHIP_STATUS[row.status]} />, exportValue: (row) => MENTORSHIP_STATUS[row.status].label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title={isOffice ? "Mentorships" : "Your mentoring"}
        description={isOffice ? "Former pupils mentoring learners in school, coordinated by the office." : "The learners you mentor for the school."}
        actions={isOffice && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { add.reset(); setAdding(true); }}><HeartHandshake className="h-4 w-4" /> New mentorship</button>}
        rows={rows}
        config={mentorshipConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["mentorship", "mentorships"]}
        exportName={{ file: "alumni-mentorships", title: "Alumni Mentorships" }}
        canExport={isOffice}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No mentorships yet."
      />
      {row && (
        <DetailsModal
          title={row.title}
          headline={{ label: labelOf(MENTORSHIP_TYPES, row.mentorship_type), value: `${row.mentor_name} → ${row.mentee_name}`, note: `From ${day(row.start_date)} · planned for ${row.expected_duration_months} months` }}
          status={MENTORSHIP_STATUS[row.status]}
          facts={[["Coordinator", row.coordinator_name], ["Ended", day(row.end_date) || "—"]]}
          sections={[{ title: "Goals", body: row.goals }, { title: "Arrangements", body: row.description }, { title: "History", body: row.notes }]}
          onClose={() => setViewing(null)}
        >
          {isOffice && mentorshipMoves(row.status).length > 0 && <MentorshipMove mentorship={row} busy={move.isPending} error={move.error} onMove={(status, reason) => move.mutate({ id: row.id, status, reason })} />}
        </DetailsModal>
      )}
      {adding && <MentorshipForm alumni={alumni} options={options} busy={add.isPending} error={add.isError ? apiErrorMessage(add.error, "The mentorship could not be started.") : undefined} onSave={(data) => add.mutate(data)} onClose={() => setAdding(false)} />}
    </div>
  );
}

function MentorshipMove({ mentorship, busy, error, onMove }: { mentorship: Mentorship; busy: boolean; error: unknown; onMove: (status: string, reason: string) => void }) {
  const moves = mentorshipMoves(mentorship.status);
  const [target, setTarget] = useState(moves[0].value);
  return (
    <ReasonPanel title="Change the mentorship" label="Reason" placeholder="What the change rests on" context={{ key: "alumni.note", params: { kind: "mentorship_change", title: mentorship.title } }} button={moves.find((item) => item.value === target)?.label ?? "Save"} danger={target === "cancelled"} busy={busy} error={error} errorText="The mentorship could not be changed." onSubmit={(reason) => onMove(target, reason)}>
      <Field label="Change to">
        <select value={target} onChange={(event) => setTarget(event.target.value as typeof target)} className="input mt-1">{moves.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
      </Field>
    </ReasonPanel>
  );
}

/** Newsletters to former pupils. */
export function NewslettersPanel({ rows, isOffice, isLoading, isError, onRetry, onChanged }: { rows?: Newsletter[]; isOffice: boolean; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const save = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Record<string, unknown> }) => (id ? alumniService.updateNewsletter(id, data) : alumniService.createNewsletter(data)),
    onSuccess: (saved, { id }) => { setForm(null); setNotice({ tone: "success", text: `${saved.title} ${id ? "saved" : saved.is_published ? "published" : "saved as a draft"}.` }); onChanged(); },
  });
  const columns: RegisterColumn<Newsletter>[] = [
    { header: "Date", cell: (row) => day(row.publication_date), exportValue: (row) => day(row.publication_date) },
    { header: "Title", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title },
    { header: "Tags", cell: (row) => row.tags.join(", ") || "—", exportValue: (row) => row.tags.join(", ") },
    { header: "By", cell: (row) => row.created_by_name, exportValue: (row) => row.created_by_name, exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot label={row.is_published ? "Published" : "Draft"} dot={row.is_published ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => (row.is_published ? "Published" : "Draft") },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Newsletters"
        description={isOffice ? "News for former pupils. Jot the points and let the assistant write them up." : "News from the school."}
        actions={isOffice && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { save.reset(); setForm("new"); }}><FilePlus2 className="h-4 w-4" /> New newsletter</button>}
        rows={rows}
        config={newsletterConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={() => "Read"}
        noun={["newsletter", "newsletters"]}
        exportName={{ file: "alumni-newsletters", title: "Alumni Newsletters" }}
        canExport={isOffice}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No newsletters yet."
      />
      {row && !form && (
        <DetailsModal
          size="3xl"
          title={row.title}
          headline={{ label: "Newsletter", value: day(row.publication_date), note: row.tags.join(" · ") || undefined }}
          status={{ label: row.is_published ? "Published" : "Draft", dot: row.is_published ? "bg-emerald-500" : "bg-gray-400" }}
          facts={[["By", row.created_by_name]]}
          sections={[{ title: "Newsletter", body: row.content }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {isOffice && <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setForm("edit"); }}><Pencil className="h-4 w-4" /> Edit</button>}
            </>
          }
        />
      )}
      {form === "new" && <NewsletterForm busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The newsletter could not be saved.") : undefined} onSave={(data) => save.mutate({ data })} onClose={() => setForm(null)} />}
      {form === "edit" && row && <NewsletterForm record={row} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The newsletter could not be saved.") : undefined} onSave={(data) => save.mutate({ id: row.id, data })} onClose={() => setForm(null)} />}
    </div>
  );
}

/** Surveys of former pupils: the office sets them and reads the answers; alumni answer once. */
export function SurveysPanel({ rows, responses, isOffice, hasProfile, isLoading, isError, onRetry, onChanged }: { rows?: Survey[]; responses: SurveyResponse[]; isOffice: boolean; hasProfile: boolean; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState<"new" | "edit" | "respond" | null>(null);
  const results = useQuery({ queryKey: ["alumni", "survey-results", viewing], queryFn: () => alumniService.getSurveyResults(viewing!), enabled: isOffice && Boolean(viewing) });
  const save = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Record<string, unknown> }) => (id ? alumniService.updateSurvey(id, data) : alumniService.createSurvey(data)),
    onSuccess: (saved, { id }) => { setForm(null); setNotice({ tone: "success", text: `${saved.title} ${id ? "saved" : "opened"}.` }); onChanged(); },
  });
  const respond = useMutation({
    mutationFn: ({ survey, answers }: { survey: number; answers: Record<string, string> }) => alumniService.respond({ survey, responses: answers }),
    onSuccess: () => { setForm(null); setViewing(null); setNotice({ tone: "success", text: "Thank you — your answers are in." }); onChanged(); },
  });
  const answered = (row: Survey) => responses.some((item) => item.survey === row.id);
  const columns: RegisterColumn<Survey>[] = [
    { header: "Survey", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title },
    { header: "Kind", cell: (row) => labelOf(SURVEY_TYPES, row.survey_type), exportValue: (row) => labelOf(SURVEY_TYPES, row.survey_type) },
    { header: "Open", cell: (row) => `${day(row.start_date)} → ${day(row.end_date)}`, exportValue: (row) => `${day(row.start_date)} to ${day(row.end_date)}` },
    { header: "Questions", cell: (row) => row.questions.length, exportValue: (row) => row.questions.length, align: "right" },
    ...(isOffice
      ? [{ header: "Answers", cell: (row: Survey) => row.response_count ?? 0, exportValue: (row: Survey) => row.response_count ?? 0, align: "right" as const }]
      : [{ header: "You", cell: (row: Survey) => (answered(row) ? <span className="text-emerald-700">Answered</span> : "—"), exportValue: (row: Survey) => (answered(row) ? "Answered" : "") }]),
    { header: "Status", cell: (row) => <StatusDot {...surveyState(row, today)} />, exportValue: (row) => surveyState(row, today).label },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const canAnswer = row && !isOffice && hasProfile && !answered(row) && surveyState(row, today).label === "Open";
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Surveys"
        description={isOffice ? "Questions for former pupils. Open one to read every answer, question by question." : "Surveys from the school. Each takes a few minutes and you answer once."}
        actions={isOffice && <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { save.reset(); setForm("new"); }}><ListChecks className="h-4 w-4" /> New survey</button>}
        rows={rows}
        config={surveyConfig(today)}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (isOffice ? "Answers" : !answered(item) && hasProfile ? "Answer" : "Open")}
        noun={["survey", "surveys"]}
        exportName={{ file: "alumni-surveys", title: "Alumni Surveys" }}
        canExport={isOffice}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={isOffice ? "No surveys yet." : "No surveys are open just now."}
      />
      {row && !form && (
        <DetailsModal
          size="3xl"
          title={row.title}
          headline={{ label: labelOf(SURVEY_TYPES, row.survey_type), value: `${row.questions.length} ${row.questions.length === 1 ? "question" : "questions"}`, note: `Open ${day(row.start_date)} to ${day(row.end_date)}` }}
          status={surveyState(row, today)}
          facts={isOffice ? [["Answers", String(row.response_count ?? 0)], ["Set by", row.created_by_name]] : [["You", answered(row) ? "Answered — thank you" : "Not answered yet"]]}
          sections={[{ title: "About it", body: row.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {isOffice && <button type="button" className="mr-auto btn btn-outline" onClick={() => save.mutate({ id: row.id, data: { is_active: !row.is_active } })}>{row.is_active ? "Close survey" : "Reopen survey"}</button>}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {isOffice && <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setForm("edit"); }}><Pencil className="h-4 w-4" /> Edit</button>}
              {canAnswer && <button type="button" className="btn btn-primary" onClick={() => { respond.reset(); setForm("respond"); }}>Answer</button>}
            </>
          }
        >
          {isOffice ? (
            results.isLoading ? <div className="h-10 animate-pulse rounded bg-gray-100" /> : (
              <div className="space-y-4">
                {results.data?.questions.map((item, index) => (
                  <section key={item.question} className="space-y-1.5">
                    <h4 className="text-sm font-semibold text-gray-900">{index + 1}. {item.question}</h4>
                    {item.answers.length ? (
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
                        {item.answers.map((answer, position) => (
                          <li key={position} className="px-3 py-2">
                            <p className="whitespace-pre-wrap break-words text-gray-800">{answer.answer}</p>
                            <p className="text-xs text-gray-500">{answer.alumni_name} · class of {answer.graduation_year}</p>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-gray-500">No answers yet.</p>}
                  </section>
                ))}
              </div>
            )
          ) : (
            <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">{row.questions.map((question) => <li key={question}>{question}</li>)}</ol>
          )}
        </DetailsModal>
      )}
      {form === "new" && <SurveyForm busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The survey could not be saved.") : undefined} onSave={(data) => save.mutate({ data })} onClose={() => setForm(null)} />}
      {form === "edit" && row && <SurveyForm record={row} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The survey could not be saved.") : undefined} onSave={(data) => save.mutate({ id: row.id, data })} onClose={() => setForm(null)} />}
      {form === "respond" && row && <RespondForm survey={row} busy={respond.isPending} error={respond.isError ? apiErrorMessage(respond.error, "Your answers could not be sent.") : undefined} onSave={(answers) => respond.mutate({ survey: row.id, answers })} onClose={() => setForm(null)} />}
    </div>
  );
}
